import i18n from "i18next";
import React, { useState } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { usePositionsGuideData } from "~/pages/Editor/EditorDirectories/hooks";
import { getTableHeader } from "~/pages/Editor/EditorDirectories/options";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import PositionsDirectoryFilter from "./Filter";
import PositionManagement from "./PositionManagement";
import {
  getPositionDirectoryColumns,
  getPositionDirectorySortableApiKeys,
} from "./model/tableAdapter";

import s from "./EditorPositionsDirectory.less";

const formId = "PositionManagementForm";

const EditorPositionsDirectory = () => {
  const [tableHeader] = useState(getTableHeader(i18n.language));
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();

  const {
    count,
    page,
    openModal,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();

  const sortableApiKeys = getPositionDirectorySortableApiKeys();

  const { sortType: normalizedSortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(sortField) ? sortField : undefined;

  const { handleCount, handlePage } = usePaginationParams();

  const openAddModal = () => setDataReplace("openModal", "Add");
  const closeModal = () => setDataReplace("openModal", "", "currentId", "");

  const { refetch, loading, tableItems, totalRecords } = usePositionsGuideData(
    page,
    count,
  );

  const columns = getPositionDirectoryColumns(
    t,
    tableHeader,
    tableSortField,
    normalizedSortType,
  );

  const handleRowEdit = (record: { id?: string | number }) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t("editor.positions_directory")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<PositionsDirectoryFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("employeeAccounting.position.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        columns={columns}
        dataSource={tableItems}
        loading={loading}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        {...(hasPermission("employeeAccounting.position.update")
          ? { onRowEdit: handleRowEdit }
          : {})}
        wrapperClassName={s.editor_table}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={totalRecords}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === "Add" || openModal === "Edit"}
        title={
          openModal === "Add"
            ? t("editor.add_position")
            : t("editor.edit_position_directory")
        }
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        <PositionManagement
          refetch={refetch}
          closeModal={closeModal}
          formId={formId}
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default EditorPositionsDirectory;
